<template>
  <section :class="$style.input">
    <header :class="[$style.input__header, $style.header]">
      <h3 :class="[$style.header__title, $style.title]">
        {{ item.title }}
      </h3>
      <p :class="$style.header__text">
        {{ item.message }}
      </p>
    </header>
    <div :class="[$style.input__ranking, $style.ranking]">
      <p
        :class="[
          $style.ranking__rank,
          $style[`ranking__rank--${rankModifier}`],
        ]"
      >
        {{ rankText }}
      </p>
      <dl
        :class="[
          $style.ranking__values,
          $style.values,
          $style[`values--${rankModifier}`],
        ]"
      >
        <div :class="$style.values__item">
          <dt :class="$style.values__title">ユーザー</dt>
          <dd
            :class="[$style.values__value, $style.value, $style['value--user']]"
          >
            {{ userValue
            }}<span :class="$style.value__unit">{{ item.unit }}</span>
          </dd>
        </div>
        <div :class="[$style.values__item, $style['values__item--average']]">
          <dt :class="$style.values__title">AVERAGE</dt>
          <dd :class="[$style.values__value, $style.value]">
            {{ averageValue
            }}<span :class="$style.value__unit">{{ item.unit }}</span>
          </dd>
        </div>
      </dl>
    </div>
  </section>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import type { ConcreteDiagnosisAnalysisItem } from '@/types/app'

const formatMoney = (value: number, unit: '万円' | '円'): string => {
  const roundedValue = Math.round(unit === '万円' ? value / 10000 : value)
  return new Intl.NumberFormat().format(roundedValue)
}

export default defineComponent({
  name: 'UserAnalysis',
  props: {
    item: {
      type: Object as PropType<ConcreteDiagnosisAnalysisItem>,
      required: true,
    },
  },
  computed: {
    rankModifier(): string {
      return this.item.rank?.toLowerCase() ?? 'norank'
    },

    rankText(): string {
      return this.item.rank ?? '--'
    },

    userValue(): string {
      return formatMoney(this.item.userValue, this.item.unit)
    },

    averageValue(): string {
      return formatMoney(this.item.averageValue, this.item.unit)
    },
  },
})
</script>

<style lang="scss" module>
.input {
  &__ranking {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }
}

.title {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
}

@mixin rank-colored {
  /* stylelint-disable selector-class-pattern, nesting-selector-no-missing-scoping-root */
  &--a {
    color: $color-label-blue;
  }

  &--b {
    color: $color-label-green;
  }

  &--c {
    color: $color-label-yellow;
  }

  &--d {
    color: $color-label-red;
  }

  &--e {
    color: $color-label-gray;
  }
  /* stylelint-enable selector-class-pattern, nesting-selector-no-missing-scoping-root */
}

.ranking {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: $color-surface-secondary;
  border-radius: $border-radius-v2-rounder;

  &__rank {
    display: flex;
    width: 40px;
    height: 50px;
    align-items: center;
    align-self: flex-start;
    justify-content: center;
    font-size: 50px;
    font-weight: bold;

    @include rank-colored;

    &--norank {
      color: $color-text-and-object-low-emphasis;
      font-size: 14px;
    }
  }

  &__values {
    flex: 1;
    align-self: center;

    &:nth-child(n + 2) {
      margin-left: 20px;
    }
  }
}

.values {
  @include rank-colored;

  &__item {
    &--average {
      color: $color-text-and-object-low-emphasis;
    }

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__title {
    font-size: 12px;
  }

  &__value {
    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.value {
  font-size: 18px;
  font-weight: bold;
  text-align: right;

  &--user {
    font-size: 24px;
  }

  &__unit {
    font-size: 12px;
    font-weight: normal;
  }
}

.header {
  &__text {
    color: $color-text-and-object-high-emphasis;
    font-size: 10px;
    line-height: 1.6;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}
</style>
